/**
 * `budgeted_hours` is flaky on the backend (BUG-07) — this module is the single place
 * where the ugly shape normalisation happens. NEVER read `recurring_ticket.budgeted_hours`
 * directly in UI code; always route through `parseBudgetedHours()`.
 *
 * Known bad shapes shipped over time:
 *  - `"04:30:00"`                    → canonical HH:MM:SS
 *  - `"04:30"`                       → HH:MM (no seconds)
 *  - `"4"`                           → bare hours
 *  - `{ hours: 4 }`                  → object with only some fields
 *  - `{ hours: "04", minutes: "30" }` → string-typed numeric fields (legacy)
 *  - `{}`                            → empty object
 *  - `null` / `undefined`
 *  - `"bad garbage"`                 → non-parseable (defensive)
 *
 * All of these resolve to either a `{hours, minutes, seconds}` triple or `null`.
 */

import type { BudgetedHoursShape } from "shared";

export interface BudgetedHoursHMS {
  hours: number;
  minutes: number;
  seconds: number;
}

const clampNonNegative = (value: number): number =>
  Number.isFinite(value) && value > 0 ? Math.trunc(value) : 0;

const coerceNumber = (value: unknown): number => {
  if (typeof value === "number") return clampNonNegative(value);
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : clampNonNegative(parsed);
  }
  return 0;
};

/**
 * Normalises any shape the backend has shipped for `budgeted_hours` into a
 * `{hours, minutes, seconds}` triple, or `null` when the value is absent /
 * entirely unparseable / all-zero.
 *
 * @example
 *   parseBudgetedHours("04:30:00")         // { hours: 4, minutes: 30, seconds: 0 }
 *   parseBudgetedHours("04:30")            // { hours: 4, minutes: 30, seconds: 0 }
 *   parseBudgetedHours({ hours: 4 })       // { hours: 4, minutes: 0, seconds: 0 }
 *   parseBudgetedHours(null)               // null
 *   parseBudgetedHours("garbage")          // null
 *   parseBudgetedHours({})                 // null
 */
export const parseBudgetedHours = (
  value: BudgetedHoursShape | undefined
): BudgetedHoursHMS | null => {
  if (value === null || value === undefined) return null;

  // String form.
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;

    // HH:MM or HH:MM:SS form.
    if (trimmed.includes(":")) {
      const parts = trimmed.split(":");
      const hours = coerceNumber(parts[0]);
      const minutes = coerceNumber(parts[1]);
      const seconds = coerceNumber(parts[2]);
      if (hours === 0 && minutes === 0 && seconds === 0) return null;
      return { hours, minutes, seconds };
    }

    // Bare numeric string (legacy: "4" → 4 hours).
    const bareNumber = Number(trimmed);
    if (!Number.isNaN(bareNumber) && bareNumber > 0) {
      return { hours: clampNonNegative(bareNumber), minutes: 0, seconds: 0 };
    }
    return null;
  }

  // Object form.
  if (typeof value === "object") {
    const hours = coerceNumber((value as { hours?: unknown }).hours);
    const minutes = coerceNumber((value as { minutes?: unknown }).minutes);
    const seconds = coerceNumber((value as { seconds?: unknown }).seconds);
    if (hours === 0 && minutes === 0 && seconds === 0) return null;
    return { hours, minutes, seconds };
  }

  return null;
};

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** Pretty-print a parsed triple (or a raw BudgetedHoursShape) as "HH:MM:SS". */
export const formatBudgetedHours = (
  value: BudgetedHoursShape | BudgetedHoursHMS | null | undefined,
  fallback: string = "—"
): string => {
  if (value === null || value === undefined) return fallback;

  // Already a parsed triple.
  if (
    typeof value === "object" &&
    value !== null &&
    "hours" in value &&
    "minutes" in value &&
    "seconds" in value
  ) {
    const triple = value as BudgetedHoursHMS;
    return `${pad2(triple.hours)}:${pad2(triple.minutes)}:${pad2(triple.seconds)}`;
  }

  const parsed = parseBudgetedHours(value as BudgetedHoursShape);
  if (!parsed) return fallback;
  return `${pad2(parsed.hours)}:${pad2(parsed.minutes)}:${pad2(parsed.seconds)}`;
};

/** Serialise a triple for API submission as `"HH:MM:SS"`. */
export const serializeBudgetedHours = (
  hms: BudgetedHoursHMS | null
): string | null => {
  if (!hms) return null;
  if (hms.hours === 0 && hms.minutes === 0 && hms.seconds === 0) return null;
  return `${pad2(hms.hours)}:${pad2(hms.minutes)}:${pad2(hms.seconds)}`;
};
